import get from 'lodash/get'

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { canCallEndpoint, getTimestampForNextApiCall } from '../../../../common/utils'
import { getTransformedFinancialOverview } from './utils-new'

// Prevents calling the API every time the user opens the wallet view.
// Instead, the API is only called if at least 5 minutes have passed since the last call.
// This helps avoid unnecessary recalculations.
// The restriction can be bypassed when certain actions occur—such as making payments
// for invoices or installments—that directly affect the user's balances/payables
const MINUTES_TO_ADD = 5

const initialState = {
  financialData: {
    accountSummary: {},
    payables: [],
    serviceFees: [],
    lateFees: [],
    virtualAccounts: []
  },
  loading: false,
  loadingVirtualAccounts: false,
  error: null,
  timestampForTheNextApiCall: 0 // next api call to get all financial data
}

export const getFinancialOverview = createAsyncThunk('wallet/getFinancesNew', async (args, thunkAPI) => {
  const { extra: { api, locationId } } = thunkAPI

  let locationIdToFetch = locationId
  if (args && args.locationId) {
    locationIdToFetch = args.locationId
  }

  // this call will only be triggered if the condition (prop) is true
  try {
    const financialOverview = await api.finances.getOverview(locationIdToFetch)
    const transformedFinancialOverview = getTransformedFinancialOverview(financialOverview)
    return {
      transformedFinancialOverview
    }
  } catch (error) {
    return error
  }
}, {
  condition: (args, thunkAPI) => {
    if (args && args.forceRefresh) return true // This ensures that the API is called

    const { getState } = thunkAPI
    const { timestampForTheNextApiCall } = getState().financesNew
    const callEndpoint = canCallEndpoint(timestampForTheNextApiCall)
    return callEndpoint
  }
})

export const getVirtualAccounts = createAsyncThunk('wallet/getVirtualAccounts', async (args, thunkAPI) => {
  const { extra: { api, locationId }, rejectWithValue } = thunkAPI

  // this call will only be triggered if the condition (prop) is true
  try {
    const response = await api.finances.getVirtualAccounts(locationId)
    if (response.error) {
      return rejectWithValue(response.error)
    }
    return response
  } catch (error) {
    return rejectWithValue(error)
  }
}, {
  condition: (args, thunkAPI) => {
    const { getState } = thunkAPI
    const { financialData } = getState().financesNew
    const virtualAccount = !!get(financialData, 'virtualAccount', [])
    return !virtualAccount.length
  }
})

export const financesSliceNew = createSlice({
  name: 'financesNew',
  initialState,
  reducers: {
    updateCreditApplicationStatus: (state, action) => {
      const creditApplicationStatus = action.payload
      state.financialData = {
        ...state.financialData,
        accountSummary: {
          ...state.financialData.accountSummary,
          creditApplicationStatus
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder

    // GETS ALL FINANCIAL DATA
    // This api call gets the user's financial overview -- balances, paytables, virtual account etc
    // It is first called when a user logs in (PrivateRoutes).
    // It can only be triggered based on a condition after the first call.
    // CONDITION: timestampForTheNextApiCall < new Date().getTime()
      .addCase(getFinancialOverview.pending, (state, action) => {
        state.loading = true
      })
      .addCase(getFinancialOverview.fulfilled, (state, action) => {
        if (action.payload) {
          const { transformedFinancialOverview } = action.payload
          const accountSummary = get(transformedFinancialOverview, 'accountSummary', {})
          const payables = get(transformedFinancialOverview, 'payables', [])
          const serviceFees = get(transformedFinancialOverview, 'serviceFees', [])
          const lateFees = get(transformedFinancialOverview, 'lateFees', [])
          const nextApiCallTimestamp = getTimestampForNextApiCall(MINUTES_TO_ADD)

          state.loading = false
          state.timestampForTheNextApiCall = nextApiCallTimestamp
          state.error = null
          state.financialData = {
            ...state.financialData,
            accountSummary,
            payables,
            serviceFees,
            lateFees
          }
        } else {
          state.loading = false
          state.timestampForTheNextApiCall = 0
          state.error = action.payload
        }
      })
      .addCase(getFinancialOverview.rejected, (state, action) => {
        state.loading = false
        state.timestampForTheNextApiCall = 0
        state.error = action.payload
      })

      // VIRTAUL ACCOUNTS
      .addCase(getVirtualAccounts.pending, (state, action) => {
        state.loadingVirtualAccounts = true
      })
      .addCase(getVirtualAccounts.fulfilled, (state, action) => {
        if (action.payload && !action.payload.error) {
          state.loadingVirtualAccounts = false
          state.error = null
          state.financialData = {
            ...state.financialData,
            virtualAccounts: action.payload
          }
        } else {
          state.loadingVirtualAccounts = false
        }
      })
  }
})

export const { updateCreditApplicationStatus } = financesSliceNew.actions

export const selectFinancialData = (state) => get(state, 'financesNew.financialData', {})
export const selectAccountSummary = (state) => get(state, 'financesNew.financialData.accountSummary', {})
export const selectPayables = (state) => get(state, 'financesNew.financialData.payables', [])
export const selectServiceFees = (state) => get(state, 'financesNew.financialData.serviceFees', [])
export const selectLateFees = (state) => get(state, 'financesNew.financialData.lateFees', [])
export const selectVirtualAccounts = (state) => get(state, 'financesNew.financialData.virtualAccounts', [])
export const selectLoadingState = (state) => get(state, 'financesNew.loading', false)
export const selectLoadingVirtualAccountsState = (state) => get(state, 'financesNew.loadingVirtualAccounts', false)
export const selectErrorState = (state) => get(state, 'financesNew.error', null)

export default financesSliceNew.reducer
